/** Stable API error codes (SPEC §8.3). */
export const ERROR_CODES = [
  "NOT_FOUND",
  "FORBIDDEN",
  "UNAUTHORIZED",
  "VALIDATION_ERROR",
  "NICKNAME_TAKEN",
  "LOBBY_CLOSED",
  "GAME_FULL",
  "INVALID_STATE",
  "ALREADY_ANSWERED",
  "ROUND_NOT_OPEN",
  "ROUND_NOT_RESOLVED",
  "GAME_NOT_FINISHED",
  "HOST_QUOTA_EXCEEDED",
  "TOO_EARLY",
  "INTERNAL",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  NOT_FOUND: 404,
  FORBIDDEN: 403,
  UNAUTHORIZED: 401,
  VALIDATION_ERROR: 400,
  NICKNAME_TAKEN: 409,
  LOBBY_CLOSED: 409,
  GAME_FULL: 409,
  INVALID_STATE: 409,
  ALREADY_ANSWERED: 409,
  ROUND_NOT_OPEN: 409,
  ROUND_NOT_RESOLVED: 409,
  GAME_NOT_FINISHED: 403,
  HOST_QUOTA_EXCEEDED: 429,
  TOO_EARLY: 409,
  INTERNAL: 500,
};

/** Finnish default messages for the UI. */
export const ERROR_MESSAGES_FI: Readonly<Record<ErrorCode, string>> = {
  NOT_FOUND: "Peliä ei löytynyt.",
  FORBIDDEN: "Ei oikeutta.",
  UNAUTHORIZED: "Kirjaudu sisään.",
  VALIDATION_ERROR: "Virheellinen syöte.",
  NICKNAME_TAKEN: "Nimimerkki on jo käytössä.",
  LOBBY_CLOSED: "Peli on jo alkanut, liittyminen on suljettu.",
  GAME_FULL: "Peli on täynnä.",
  INVALID_STATE: "Toiminto ei ole mahdollinen pelin nykyisessä tilassa.",
  ALREADY_ANSWERED: "Olet jo valinnut saaliin tällä kierroksella.",
  ROUND_NOT_OPEN: "Kierros ei ole auki.",
  ROUND_NOT_RESOLVED: "Kierroksen tulokset eivät ole vielä valmiit.",
  GAME_NOT_FINISHED: "Peli ei ole vielä päättynyt.",
  HOST_QUOTA_EXCEEDED: "Päivän peliraja on täynnä.",
  TOO_EARLY: "Liian aikaista, odota hetki.",
  INTERNAL: "Palvelinvirhe. Yritä uudelleen.",
};

export interface ErrorBody {
  error: { code: ErrorCode; message: string };
}

export class ApiError extends Error {
  readonly status: number;
  constructor(
    readonly code: ErrorCode,
    message?: string,
  ) {
    super(message ?? ERROR_MESSAGES_FI[code]);
    this.name = "ApiError";
    this.status = ERROR_STATUS[code];
  }

  toBody(): ErrorBody {
    return { error: { code: this.code, message: this.message } };
  }
}

export const isErrorCode = (x: unknown): x is ErrorCode => (ERROR_CODES as readonly unknown[]).includes(x);
